import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/common/Spinner";
import { ContextMenu } from "@/components/common/ContextMenu";
import { FilterButton } from "@/components/common/FilterButton";
import { IconButton } from "@/components/common/IconButton";
import { IncludeArchivedCheckbox } from "@/components/common/IncludeArchivedCheckbox";
import { BAR_COLOR, BarTrack, MIN_BAR_SEGMENT_WIDTH } from "@/components/billing/BarTrack";
import { BillingFilterUrlSync } from "@/components/billing/BillingFilterUrlSync";
import { BillingUsageChart } from "@/components/billing/BillingUsageChart";
import { CreditGrantsDialog } from "@/components/billing/CreditGrantsDialog";
import {
	ProjectCreditLimitBadge,
	ProjectCreditLimitSummary,
} from "@/components/billing/ProjectCreditLimit";
import {
	downloadBillingEventsCsv,
	getBillingDaily,
	getBillingSummary,
	getCreditBalance,
	getProjectCreditLimits,
	listOrganizations,
	type BillingDailyCredits,
	type BillingProjectCredits,
	type BillingSummary,
	type CreditBalance,
	type Organization,
	type ProjectCreditLimit,
} from "@/lib/api";
import { downloadBlob } from "@/lib/download-file";
import { useBillingFilterStore } from "@/stores/useBillingFilterStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";
import {
	BILLING_ORIGIN_NAMES,
	BILLING_CATEGORIES,
	BILLING_PERIODS,
	billingCsvFilename,
	billingPeriodStart,
	categoryIdsToEventTypes,
	formatCredits,
	groupCreditsByEnvironment,
	projectCreditLimitStatus,
	type BillingCategoryBreakdown,
	type BillingEnvironmentBreakdown,
	type BillingPeriod,
} from "@/lib/billing";

/**
 * 課金ページ。ローカル環境の課金をクラウドで管理する構成では案内だけを表示する。
 */
export function BillingPage() {
	const billingManagedInCloud = useCurrentUserStore((state) => state.billingManagedInCloud);
	return billingManagedInCloud ? <CloudBillingGuide /> : <BillingDashboard />;
}

function CloudBillingGuide() {
	return (
		<div
			data-testid="billing-cloud-guide"
			style={{
				padding: 32,
				height: "100%",
				overflowY: "auto",
				background: "var(--surface-1)",
			}}
		>
			<div
				style={{
					maxWidth: 560,
					padding: 24,
					borderRadius: "var(--radius-4)",
					background: "var(--surface-2)",
					boxShadow: "var(--shadow-ring)",
				}}
			>
				<h2
					style={{
						margin: 0,
						fontSize: "var(--text-title)",
						fontWeight: 600,
						letterSpacing: "var(--tracking-snug)",
						color: "var(--fg-1)",
					}}
				>
					課金
				</h2>
				<p
					style={{
						margin: "12px 0 0",
						fontSize: "var(--text-ui)",
						lineHeight: "var(--leading-body)",
						color: "var(--fg-2)",
					}}
				>
					クラウド版で請求・残高を確認してください。アクセス権がない場合は管理者に確認してください。
				</p>
			</div>
		</div>
	);
}

/**
 * 課金ダッシュボードのメインビュー。上部の期間・カテゴリフィルタで絞り込んだ
 * クレジット消費の合計とプロジェクト別内訳を表示する。
 */
function BillingDashboard() {
	const period = useBillingFilterStore((s) => s.period);
	const setPeriod = useBillingFilterStore((s) => s.setPeriod);
	const categoryIds = useBillingFilterStore((s) => s.categoryIds);
	const setCategoryIds = useBillingFilterStore((s) => s.setCategoryIds);
	const selectedOrganizationId = useBillingFilterStore((s) => s.organizationId);
	const selectedOrganizationName = useBillingFilterStore((s) => s.organizationName);
	const setOrganization = useBillingFilterStore((s) => s.setOrganization);

	const role = useCurrentUserStore((s) => s.role);
	const ownOrganizationId = useCurrentUserStore((s) => s.organizationId);
	const ownOrganizationName = useCurrentUserStore((s) => s.organizationName);
	const isSuperAdmin = role === "SuperAdmin";
	// 超管理者だけが組織を切り替えられる。未選択なら自組織を見る。
	const organizationId = selectedOrganizationId || ownOrganizationId;
	const organizationName = selectedOrganizationName || ownOrganizationName;

	const [summary, setSummary] = useState<BillingSummary | null>(null);
	const [daily, setDaily] = useState<BillingDailyCredits[] | null>(null);
	const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null);
	const [projectLimits, setProjectLimits] = useState<ProjectCreditLimit[] | null>(null);
	const [projectLimitsError, setProjectLimitsError] = useState<string | null>(null);
	// グラフの軸は取得時の期間で描くため、取得に使った境界を集計結果と一緒に保持する。
	const [range, setRange] = useState<{ from: Date | null; now: Date } | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setError(null);
		setLoading(true);
		const now = new Date();
		const from = billingPeriodStart(period, now);
		const params = {
			from: from?.toISOString(),
			eventTypes: categoryIdsToEventTypes(categoryIds),
			organizationId: organizationId || undefined,
		};
		// 残高は期間・カテゴリの影響を受けない全期間の値だが、更新操作を 1 つに保つため
		// 同じ effect でまとめて取り直す (クエリは軽い)。
		Promise.all([
			getBillingSummary(params),
			getBillingDaily(params),
			getCreditBalance({ organizationId: organizationId || undefined }),
		])
			.then(([summaryData, dailyData, balanceData]) => {
				if (cancelled) return;
				setSummary(summaryData);
				setDaily(dailyData);
				setCreditBalance(balanceData);
				setRange({ from, now });
			})
			.catch((err: Error) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [period, categoryIds, organizationId, refreshKey]);

	// 上限は期間・カテゴリに関係しない。取得に失敗しても消費の集計は見せられるよう、別に取ってプロジェクト別の一覧で知らせる。
	useEffect(() => {
		let cancelled = false;
		setProjectLimitsError(null);
		getProjectCreditLimits({ organizationId: organizationId || undefined })
			.then((limits) => {
				if (!cancelled) setProjectLimits(limits);
			})
			.catch((err: Error) => {
				if (!cancelled) setProjectLimitsError(err.message);
			});
		return () => {
			cancelled = true;
		};
	}, [organizationId, refreshKey]);

	return (
		<div
			data-testid="billing-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<BillingFilterUrlSync />
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					height: 40,
					flexShrink: 0,
					marginBottom: 24,
				}}
			>
				<h2
					style={{
						fontSize: "var(--text-title)",
						fontWeight: 600,
						letterSpacing: "var(--tracking-snug)",
						color: "var(--fg-1)",
						margin: 0,
						marginRight: 8,
					}}
				>
					課金
				</h2>
				{isSuperAdmin && (
					<OrganizationSelector
						organizationId={organizationId}
						organizationName={organizationName}
						onSelect={setOrganization}
					/>
				)}
				<PeriodSelector period={period} onSelect={setPeriod} />
				<CategorySelector selected={categoryIds} onChange={setCategoryIds} />
				<div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
					<IconButton
						icon="rotate"
						title="集計を更新"
						data-testid="billing-refresh"
						size={28}
						disabled={loading}
						onClick={() => setRefreshKey((k) => k + 1)}
					/>
					<BillingMenu
						period={period}
						categoryIds={categoryIds}
						organizationId={organizationId}
						disabled={loading}
					/>
				</div>
			</div>

			{error && <p style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}>{error}</p>}

			{!error && loading && summary === null && <CenteredSpinner />}

			{!error && summary !== null && daily !== null && creditBalance !== null && range !== null && (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 32,
						opacity: loading ? 0.6 : 1,
					}}
				>
					{/* 合計カードの右の残り幅を使用量グラフが使う。狭ければグラフは下へ折り返す。 */}
					<div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "stretch" }}>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 8,
								width: 320,
								flexShrink: 0,
							}}
						>
							<TotalCreditsCard totalCredits={summary.totalCredits} />
							<CreditBalanceCard
								balance={creditBalance}
								organizationId={organizationId}
								organizationName={organizationName}
								isSuperAdmin={isSuperAdmin}
								onGranted={() => setRefreshKey((k) => k + 1)}
							/>
							<FreshnessNote />
						</div>
						<BillingUsageChart rows={daily} from={range.from} now={range.now} />
					</div>
					<CreditsByProjectSection
						summary={summary}
						limits={projectLimits ?? []}
						limitsError={projectLimitsError}
						onLimitSaved={() => setRefreshKey((k) => k + 1)}
					/>
				</div>
			)}
		</div>
	);
}

/**
 * 集計対象の組織のセレクタ。超管理者のみに表示され、常に 1 組織だけを選択する。
 * 選択肢は超管理者専用の GET /api/organizations から取る。
 */
function OrganizationSelector({
	organizationId,
	organizationName,
	onSelect,
}: {
	organizationId: string;
	organizationName: string;
	onSelect: (organizationId: string, organizationName: string) => void;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	// 一覧が届くまでは null (取得前と「該当なし」を区別するため)。
	const [organizations, setOrganizations] = useState<Organization[] | null>(null);
	const anchorRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		let cancelled = false;
		// 失敗時はブラウザの未処理 rejection としてそのまま顕在化させる。
		// 一覧が空でも自組織の集計は表示できる。
		listOrganizations().then((orgs) => {
			if (!cancelled) setOrganizations(orgs);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	// URL から組織 ID だけを復元した場合、ラベルに出す表示名は一覧が届くまで分からない。
	// 届いた時点で選択中の組織の名前を書き戻す。一覧に該当が無ければ (削除済みの組織など)
	// 自組織へ戻す。これをしないとラベルは自組織名のままなのに集計は URL の組織 ID を
	// 対象に取り続け、表示と実データがずれたまま固定されてしまう。
	useEffect(() => {
		if (organizations === null) return;
		const selected = organizations.find((o) => o.id === organizationId);
		if (selected) {
			if (selected.name !== organizationName) onSelect(selected.id, selected.name);
		} else if (organizationId) {
			onSelect("", "");
		}
	}, [organizations, organizationId, organizationName, onSelect]);

	return (
		<>
			<FilterButton
				open={menuOpen}
				ref={anchorRef}
				icon="grid"
				label={organizationName}
				testId="billing-organization-selector"
				onClick={() => setMenuOpen((v) => !v)}
			/>
			{menuOpen && anchorRef.current && (
				<ContextMenu
					position={{ anchor: anchorRef.current, align: "left" }}
					onClose={() => setMenuOpen(false)}
					testId="billing-organization-menu"
					minWidth={220}
					items={(organizations ?? []).map((o) => ({
						key: o.id,
						label: o.name,
						selectable: true,
						selected: o.id === organizationId,
						testId: `billing-organization-item-${o.id}`,
						onSelect: () => onSelect(o.id, o.name),
					}))}
				/>
			)}
		</>
	);
}

/** 集計期間のセレクタ。クリックでプリセット一覧のメニューを開く。 */
export function PeriodSelector({
	period,
	onSelect,
}: {
	period: BillingPeriod;
	onSelect: (period: BillingPeriod) => void;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);
	const selected = BILLING_PERIODS.find((p) => p.id === period);

	return (
		<>
			<FilterButton
				open={menuOpen}
				ref={anchorRef}
				icon="clock"
				label={selected?.label ?? ""}
				testId="billing-period-selector"
				onClick={() => setMenuOpen((v) => !v)}
			/>
			{menuOpen && anchorRef.current && (
				<ContextMenu
					position={{ anchor: anchorRef.current, align: "left" }}
					onClose={() => setMenuOpen(false)}
					testId="billing-period-menu"
					minWidth={180}
					items={BILLING_PERIODS.map((p) => ({
						key: p.id,
						label: p.label,
						selectable: true,
						selected: p.id === period,
						testId: `billing-period-item-${p.id}`,
						onSelect: () => onSelect(p.id),
					}))}
				/>
			)}
		</>
	);
}

/** 課金カテゴリの複数選択セレクタ。選択なしは「全カテゴリ」を意味する。 */
function CategorySelector({
	selected,
	onChange,
}: {
	selected: string[];
	onChange: (categoryIds: string[]) => void;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);

	const label =
		selected.length === 0
			? "すべてのカテゴリ"
			: selected.length === 1
				? (BILLING_CATEGORIES.find((c) => c.id === selected[0])?.label ?? selected[0])
				: `カテゴリ (${selected.length})`;

	const toggle = (id: string) =>
		onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

	return (
		<>
			<FilterButton
				open={menuOpen}
				ref={anchorRef}
				icon="filter"
				label={label}
				testId="billing-category-selector"
				onClick={() => setMenuOpen((v) => !v)}
			/>
			{menuOpen && anchorRef.current && (
				<ContextMenu
					position={{ anchor: anchorRef.current, align: "left" }}
					onClose={() => setMenuOpen(false)}
					testId="billing-category-menu"
					minWidth={220}
					items={[
						{
							key: "all",
							label: "すべてのカテゴリ",
							selectable: true,
							selected: selected.length === 0,
							closeOnSelect: false,
							testId: "billing-category-item-all",
							onSelect: () => onChange([]),
						},
						{ separator: true },
						...BILLING_CATEGORIES.map((c) => ({
							key: c.id,
							label: c.label,
							selectable: true,
							selected: selected.includes(c.id),
							closeOnSelect: false,
							testId: `billing-category-item-${c.id}`,
							onSelect: () => toggle(c.id),
						})),
					]}
				/>
			)}
		</>
	);
}

/**
 * ヘッダ右端のメニュー。表示中の絞り込みに対する操作を集約する。CSV エクスポートは
 * 集計後の数字では追えない「どの行がいくら積まれたか」を検算するためのもの。
 */
function BillingMenu({
	period,
	categoryIds,
	organizationId,
	disabled,
}: {
	period: BillingPeriod;
	categoryIds: string[];
	organizationId: string;
	disabled: boolean;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const [exporting, setExporting] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);

	const exportCsv = async () => {
		setExporting(true);
		try {
			const from = billingPeriodStart(period, new Date());
			const csv = await downloadBillingEventsCsv({
				from: from?.toISOString(),
				eventTypes: categoryIdsToEventTypes(categoryIds),
				organizationId: organizationId || undefined,
			});
			downloadBlob(csv, billingCsvFilename(new Date()));
		} catch (err) {
			// 対象が多すぎる場合など、理由はバックエンドが日本語で返すのでそのまま見せる。
			useToastStore.getState().show({ type: "error", message: (err as Error).message });
			console.error("課金イベントのCSVエクスポートに失敗しました。", err);
			throw err;
		} finally {
			setExporting(false);
		}
	};

	return (
		<>
			<IconButton
				ref={anchorRef}
				icon="kebab"
				title="メニュー"
				data-testid="billing-menu-button"
				size={28}
				disabled={disabled}
				onClick={() => setMenuOpen((v) => !v)}
			/>
			{menuOpen && anchorRef.current && (
				<ContextMenu
					position={{ anchor: anchorRef.current }}
					onClose={() => setMenuOpen(false)}
					testId="billing-menu"
					minWidth={200}
					items={[
						{
							key: "pricing",
							label: "料金ガイド",
							testId: "billing-pricing-guide",
							routerLink: "/docs/pricing",
							openInNewTab: true,
						},
						{
							key: "exportCsv",
							// 書き出し中にメニューを開き直したときへの表示。選択でメニューは閉じる。
							label: exporting ? "書き出し中…" : "CSVエクスポート",
							disabled: exporting,
							testId: "billing-export-csv",
							onSelect: () => {
								void exportCsv();
							},
						},
					]}
				/>
			)}
		</>
	);
}

/** 合計消費クレジットのサマリーカード。 */
function TotalCreditsCard({ totalCredits }: { totalCredits: number }) {
	return (
		<section
			data-testid="billing-total-credits"
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 8,
				padding: 16,
				maxWidth: 320,
				background: "var(--surface-1)",
				boxShadow: "var(--shadow-ring)",
				borderRadius: "var(--radius-4)",
			}}
		>
			<span
				style={{
					fontSize: "var(--text-ui)",
					fontWeight: 600,
					color: "var(--fg-2)",
					letterSpacing: "var(--tracking-heading)",
				}}
			>
				合計消費クレジット
			</span>
			<span
				data-testid="billing-total-credits-value"
				style={{
					fontSize: "var(--text-hero)",
					fontWeight: 600,
					letterSpacing: "var(--tracking-snug)",
					color: "var(--fg-1)",
				}}
			>
				{formatCredits(totalCredits)}
				<span
					style={{
						fontSize: "var(--text-ui)",
						fontWeight: 500,
						color: "var(--fg-3)",
						marginLeft: 6,
					}}
				>
					クレジット
				</span>
			</span>
		</section>
	);
}

/**
 * クレジット残高のカード。付与合計 − 全期間の消費合計で、上のフィルタ (期間・カテゴリ)
 * の影響は受けない。残高による制限はしないため負値になり得る (負値は赤字で示すだけ)。
 * 「付与と履歴」は超管理者だけに出し、外部での入金の確認後に付与を計上できる。
 */
function CreditBalanceCard({
	balance,
	organizationId,
	organizationName,
	isSuperAdmin,
	onGranted,
}: {
	balance: CreditBalance;
	organizationId: string;
	organizationName: string;
	isSuperAdmin: boolean;
	onGranted: () => void;
}) {
	const [grantsOpen, setGrantsOpen] = useState(false);
	const negative = balance.balanceCredits < 0;

	return (
		<section
			data-testid="billing-credit-balance"
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 8,
				padding: 16,
				maxWidth: 320,
				background: "var(--surface-1)",
				boxShadow: "var(--shadow-ring)",
				borderRadius: "var(--radius-4)",
			}}
		>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<span
					style={{
						fontSize: "var(--text-ui)",
						fontWeight: 600,
						color: "var(--fg-2)",
						letterSpacing: "var(--tracking-heading)",
					}}
				>
					クレジット残高
				</span>
				{isSuperAdmin && (
					<button
						type="button"
						data-testid="billing-open-credit-grants"
						onClick={() => setGrantsOpen(true)}
						style={{
							height: 24,
							padding: "0 8px",
							background: "transparent",
							color: "var(--fg-2)",
							border: 0,
							borderRadius: "var(--radius-3)",
							fontSize: "var(--text-caption)",
							fontWeight: 500,
							fontFamily: "var(--font-sans)",
							cursor: "pointer",
						}}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLButtonElement).style.background = "var(--surface-2)";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLButtonElement).style.background = "transparent";
						}}
					>
						付与と履歴
					</button>
				)}
			</div>
			<span
				data-testid="billing-credit-balance-value"
				style={{
					fontSize: "var(--text-hero)",
					fontWeight: 600,
					letterSpacing: "var(--tracking-snug)",
					color: negative ? "var(--danger)" : "var(--fg-1)",
				}}
			>
				{formatCredits(balance.balanceCredits)}
				<span
					style={{
						fontSize: "var(--text-ui)",
						fontWeight: 500,
						color: "var(--fg-3)",
						marginLeft: 6,
					}}
				>
					クレジット
				</span>
			</span>
			<span
				style={{
					fontSize: "var(--text-caption)",
					color: "var(--fg-3)",
					fontVariantNumeric: "tabular-nums",
				}}
			>
				付与合計 {formatCredits(balance.grantedCredits)} − 全期間の消費{" "}
				{formatCredits(balance.consumedCredits)}
				{balance.expiredCredits > 0 && ` − 失効 ${formatCredits(balance.expiredCredits)}`}
			</span>
			{isSuperAdmin && (
				<CreditGrantsDialog
					open={grantsOpen}
					onClose={() => setGrantsOpen(false)}
					organizationId={organizationId}
					organizationName={organizationName}
					balance={balance}
					onGranted={onGranted}
				/>
			)}
		</section>
	);
}

/**
 * 集計基準についての注記。期間の絞り込みは日本時間 (JST) の暦で行うため明示する。
 * 環境維持の消費は 1 時間単位で確定してから台帳へ計上するため、リアルタイムに積まれる
 * LLM 利用料と同じ鮮度ではない。同じ数字の中で鮮度が混ざるので、説明しないと
 * 「合計が合わない」という誤解になる。
 */
function FreshnessNote() {
	return (
		<p
			data-testid="billing-freshness-note"
			style={{
				margin: 0,
				fontSize: "var(--text-caption)",
				lineHeight: "var(--leading-normal)",
				color: "var(--fg-3)",
			}}
		>
			集計期間は日本時間 (JST) 基準です。サーバー基本料金の使用量は最大1時間ほど遅れて反映されます。
		</p>
	);
}

/**
 * プロジェクト別の消費カード共通のラベル・小計・バーの 3 列。カードごとに列幅を
 * 決めるとバーの開始位置がカード間でずれて比較しづらいため、一覧側で列を持ち、
 * 各カードは subgrid でこの列に乗る。ラベル・小計列は全カードの最長行に揃う。
 */
const PROJECT_CARD_COLUMNS = "max-content max-content 1fr";

/**
 * プロジェクト別の消費クレジット一覧。消費量の降順のカードで、組織合計に対する
 * 割合バーとカテゴリ別の内訳バーと、有効なプロジェクトにはクレジット上限を表示する。
 * 上限を設定できるよう、対象期間に消費のない有効なプロジェクトも 0 のカードで末尾に並べる。
 * アーカイブ済みのプロジェクトのカードは隠せるが、合計消費クレジットからは差し引かない
 * (台帳の消費は残るため)。
 */
function CreditsByProjectSection({
	summary,
	limits,
	limitsError,
	onLimitSaved,
}: {
	summary: BillingSummary;
	limits: ProjectCreditLimit[];
	limitsError: string | null;
	onLimitSaved: () => void;
}) {
	const limitsByProjectId = new Map(limits.map((limit) => [limit.projectId, limit]));
	const includeArchived = useBillingFilterStore((s) => s.includeArchived);
	const setIncludeArchived = useBillingFilterStore((s) => s.setIncludeArchived);
	const consumedProjectIds = new Set(summary.byProject.map((row) => row.projectId));
	const rows = [
		...(includeArchived
			? summary.byProject
			: summary.byProject.filter((row) => row.projectArchivedAt === null)),
		...limits
			.filter((limit) => !consumedProjectIds.has(limit.projectId))
			.map(
				(limit): BillingProjectCredits => ({
					projectId: limit.projectId,
					origin: null,
					projectName: limit.projectName,
					projectArchivedAt: null,
					credits: 0,
					byEvent: [],
				}),
			),
	];

	return (
		<section data-testid="billing-by-project">
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 16,
					margin: "0 0 12px",
				}}
			>
				<h3
					style={{
						fontSize: "var(--text-ui)",
						fontWeight: 600,
						color: "var(--fg-2)",
						letterSpacing: "var(--tracking-heading)",
						margin: 0,
					}}
				>
					プロジェクト別消費クレジット
				</h3>
				<IncludeArchivedCheckbox
					checked={includeArchived}
					testId="billing-include-archived"
					onChange={setIncludeArchived}
				/>
			</div>
			{limitsError && (
				<p style={{ margin: "0 0 12px", color: "var(--danger)", fontSize: "var(--text-ui)" }}>
					{limitsError}
				</p>
			)}
			{summary.byProject.length === 0 && rows.length === 0 && (
				<p style={{ color: "var(--fg-3)", fontSize: "var(--text-ui)", padding: "8px 0" }}>
					対象期間のクレジット消費はありません
				</p>
			)}
			{summary.byProject.length > 0 && rows.length === 0 && (
				<p style={{ color: "var(--fg-3)", fontSize: "var(--text-ui)", padding: "8px 0" }}>
					対象期間の消費はアーカイブ済みのプロジェクトのみです
				</p>
			)}
			{rows.length > 0 && (
				<div
					style={{
						display: "grid",
						gridTemplateColumns: PROJECT_CARD_COLUMNS,
						rowGap: 12,
					}}
				>
					{rows.map((row) => (
						<ProjectCreditsCard
							key={projectRowKey(row)}
							row={row}
							limit={row.projectId === null ? undefined : limitsByProjectId.get(row.projectId)}
							onLimitSaved={onLimitSaved}
						/>
					))}
				</div>
			)}
		</section>
	);
}

function projectRowKey(row: BillingProjectCredits): string {
	return row.projectId ?? row.origin ?? "none";
}

/** アーカイブ済みプロジェクトの名前に添える注記。 */
const ARCHIVED_NAME_SUFFIX = " (アーカイブ済み)";

/**
 * プロジェクト 1 件分の消費カード。合計行の下に環境ごとのグループを並べ、各環境の
 * 中にカテゴリ別バーを置く。バーはすべてプロジェクト合計を基準に描くため、環境間でも
 * カテゴリ間でも長さをそのまま比較でき、全バーの長さの合計がトラック全幅 (= 100%)
 * になる。列は一覧の grid から subgrid で借りるので、バーの開始位置は全カードで揃う。
 */
function ProjectCreditsCard({
	row,
	limit,
	onLimitSaved,
}: {
	row: BillingProjectCredits;
	limit: ProjectCreditLimit | undefined;
	onLimitSaved: () => void;
}) {
	const baseName =
		row.projectName ??
		(row.projectId === null
			? BILLING_ORIGIN_NAMES[row.origin ?? "none"]
			: "(削除されたプロジェクト)");
	const archived = row.projectArchivedAt !== null;
	// 名前が長くて省略表示になったときのため、ツールチップには接尾辞まで含めた全文を出す。
	const fullName = archived ? `${baseName}${ARCHIVED_NAME_SUFFIX}` : baseName;
	const environments = groupCreditsByEnvironment(row.byEvent);

	return (
		<div
			data-testid={`billing-project-card-${projectRowKey(row)}`}
			style={{
				display: "grid",
				gridColumn: "1 / -1",
				gridTemplateColumns: "subgrid",
				alignItems: "center",
				columnGap: 12,
				rowGap: 6,
				padding: 16,
				background: "var(--surface-1)",
				boxShadow: "var(--shadow-ring)",
				borderRadius: "var(--radius-4)",
			}}
		>
			<div
				style={{
					gridColumn: "1 / -1",
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					gap: 12,
					// 行間 6px に足して、見出しと内訳の間だけ広く空ける。
					marginBottom: 6,
				}}
			>
				<span
					style={{
						minWidth: 0,
						fontSize: "var(--text-ui)",
						fontWeight: 600,
						color: row.projectName ? "var(--fg-1)" : "var(--fg-3)",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
					title={fullName}
					data-tooltip-if-truncated
				>
					{baseName}
					{archived && <span style={{ color: "var(--fg-3)" }}>{ARCHIVED_NAME_SUFFIX}</span>}
				</span>
				{limit && (
					<span style={{ flexShrink: 0, marginRight: "auto" }}>
						<ProjectCreditLimitBadge status={projectCreditLimitStatus(limit)} />
					</span>
				)}
				<span
					style={{
						flexShrink: 0,
						fontSize: "var(--text-ui)",
						fontWeight: 600,
						color: "var(--fg-1)",
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{formatCredits(row.credits)}
					<span
						style={{
							fontSize: "var(--text-caption)",
							fontWeight: 500,
							color: "var(--fg-3)",
							marginLeft: 4,
						}}
					>
						クレジット
					</span>
				</span>
			</div>

			{/* 100% の長さを目で示す基準行。以降の環境・カテゴリ行は同じ尺度で描かれる。 */}
			{environments.length > 0 && <TotalBar credits={row.credits} />}
			{environments.map((environment) => (
				<EnvironmentRows
					key={environment.environmentId ?? "none"}
					environment={environment}
					projectTotal={row.credits}
				/>
			))}
			{limit && <ProjectCreditLimitSummary limit={limit} onSaved={onLimitSaved} />}
		</div>
	);
}

/**
 * カテゴリ行と同じ体裁でプロジェクト合計を全幅で示す基準行。カテゴリバーが
 * どこまで伸びれば 100% なのかを目で分かるようにする。
 */
function TotalBar({ credits }: { credits: number }) {
	return (
		<div data-testid="billing-total-bar" style={{ display: "contents" }}>
			<span
				style={{
					fontSize: "var(--text-caption)",
					fontWeight: 600,
					color: "var(--fg-2)",
					whiteSpace: "nowrap",
				}}
			>
				合計
			</span>
			<span
				style={{
					textAlign: "right",
					fontSize: "var(--text-caption)",
					fontWeight: 600,
					color: "var(--fg-2)",
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{formatCredits(credits)}
			</span>
			<BarTrack>
				<div
					style={{
						width: "100%",
						height: "100%",
						borderRadius: "var(--radius-full)",
						background: BAR_COLOR,
					}}
				/>
			</BarTrack>
		</div>
	);
}

/**
 * 環境 1 件分の行群。環境の小計行に続けて、その環境のカテゴリ別バーを字下げして並べる。
 * 親の grid へ直接セルを流し込むため、行の区切りは字下げと文字の強さだけで示す。
 */
function EnvironmentRows({
	environment,
	projectTotal,
}: {
	environment: BillingEnvironmentBreakdown;
	projectTotal: number;
}) {
	return (
		<div
			data-testid={`billing-environment-${environment.environmentId ?? "none"}`}
			style={{ display: "contents" }}
		>
			<span
				style={{
					paddingTop: 6,
					fontSize: "var(--text-caption)",
					fontWeight: 600,
					color: "var(--fg-2)",
					whiteSpace: "nowrap",
				}}
			>
				{environment.label}
			</span>
			<span
				style={{
					paddingTop: 6,
					textAlign: "right",
					fontSize: "var(--text-caption)",
					fontWeight: 600,
					color: "var(--fg-2)",
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{formatCredits(environment.credits)}
			</span>
			<div style={{ paddingTop: 6 }}>
				<BarTrack>
					<div
						style={{
							width: `${(projectTotal > 0 ? environment.credits / projectTotal : 0) * 100}%`,
							minWidth: MIN_BAR_SEGMENT_WIDTH,
							height: "100%",
							borderRadius: "var(--radius-full)",
							background: BAR_COLOR,
						}}
					/>
				</BarTrack>
			</div>
			{environment.categories.map((b) => (
				<CategoryBar key={b.category.id} breakdown={b} projectTotal={projectTotal} />
			))}
		</div>
	);
}

/**
 * カテゴリ 1 件分の内訳バー。親の grid にラベル・小計・バーの 3 セルを提供する
 * (display: contents)。バーの長さはプロジェクト合計に対する割合なので、カテゴリ間で
 * 相対的な大きさを比較できる。カテゴリの分割軸 (イベント種別 / 環境) が複数あれば
 * セグメント分割して示す。
 */
function CategoryBar({
	breakdown,
	projectTotal,
}: {
	breakdown: BillingCategoryBreakdown;
	projectTotal: number;
}) {
	const { category, credits, segments } = breakdown;
	return (
		<div data-testid={`billing-category-bar-${category.id}`} style={{ display: "contents" }}>
			<span
				style={{
					paddingLeft: 12,
					fontSize: "var(--text-caption)",
					color: "var(--fg-2)",
					whiteSpace: "nowrap",
				}}
			>
				{category.label}
			</span>
			{/* 小計はバーの左に置き、バーの右端をプロジェクト合計バーと揃える。 */}
			<span
				style={{
					textAlign: "right",
					fontSize: "var(--text-caption)",
					color: "var(--fg-2)",
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{formatCredits(credits)}
			</span>
			<BarTrack>
				{segments.map((segment) => (
					<div
						key={segment.key}
						data-testid={`billing-segment-${segment.key}`}
						title={`${segment.label}: ${formatCredits(segment.credits)}クレジット`}
						data-tooltip-delay={0}
						style={{
							width: `${(projectTotal > 0 ? segment.credits / projectTotal : 0) * 100}%`,
							minWidth: MIN_BAR_SEGMENT_WIDTH,
							height: "100%",
							borderRadius: "var(--radius-full)",
							background: BAR_COLOR,
						}}
						onMouseEnter={(e) => {
							(e.currentTarget as HTMLDivElement).style.opacity = "0.65";
						}}
						onMouseLeave={(e) => {
							(e.currentTarget as HTMLDivElement).style.opacity = "1";
						}}
					/>
				))}
			</BarTrack>
		</div>
	);
}

function CenteredSpinner() {
	return (
		<div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
			<Spinner size={16} />
		</div>
	);
}
